import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { MoneyLedger } from '@/components/MoneyLedger';

interface WalletData {
  balanceGBP: number;
  pendingHoldGBP: number;
  lifetimeEarnedGBP?: number;
  transactions: { id: string; date: string; description: string; amountGBP: number; type: string }[];
  payoutMethod?: { kind: 'BANK'; last4: string };
  autoWithdraw?: { enabled: boolean; minGBP: number };
}


// A fresh account starts empty - real data from /me/wallet fills it in as the
// member buys and sells. No demo numbers.
const EMPTY_WALLET: WalletData = {
  balanceGBP: 0,
  pendingHoldGBP: 0,
  lifetimeEarnedGBP: 0,
  transactions: [],
};

/** Bucket the real transaction ledger into 12 weekly totals for the earnings chart. */
function buildWeeklyEarnings(transactions: WalletData['transactions']) {
  const WEEKS = 12;
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const rangeStart = Date.now() - WEEKS * weekMs;
  const buckets = Array.from({ length: WEEKS }, () => ({ sales: 0, swap: 0, refund: 0 }));

  for (const t of transactions) {
    const ts = new Date(t.date).getTime();
    if (Number.isNaN(ts) || ts < rangeStart) continue;
    const idx = Math.min(WEEKS - 1, Math.floor((ts - rangeStart) / weekMs));
    const b = buckets[idx];
    if (t.type === 'CREDIT' || t.type === 'RELEASE') b.sales += t.amountGBP;
    else if (t.type === 'SWAP') b.swap += Math.abs(t.amountGBP);
    else if (t.type === 'REFUND') b.refund += Math.abs(t.amountGBP);
  }
  return buckets;
}

export default function Wallet() {
  const qc = useQueryClient();
  const [withdraw, setWithdraw] = useState<number | ''>('');
  const [ledgerFilter, setLedgerFilter] = useState<'all' | 'credits' | 'fees' | 'payouts'>('all');
  const [showBank, setShowBank] = useState(false);
  const [bankName, setBankName] = useState('');
  const [bankSort, setBankSort] = useState('');
  const [bankNumber, setBankNumber] = useState('');

  const { data: walletData } = useQuery<WalletData>({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get('/me/wallet')).data,
    retry: false,
  });
  const data = walletData ?? EMPTY_WALLET;

  const requestPayout = useMutation({
    mutationFn: async (amount: number) => (await api.post('/me/wallet/withdraw', { amountGBP: amount })).data,
    onSuccess: (res: any) => {
      const amt = `£${Number(res?.amountGBP ?? 0).toFixed(2)}`;
      toast.success(
        res?.automatic
          ? `${amt} sent to your bank automatically. ${res?.eta ?? ''}`.trim()
          : `Payout requested - we'll review it and send ${amt} to your bank, usually within 1-3 business days.`,
      );
      // Refresh wallet + header balance so the money is no longer shown as available.
      qc.invalidateQueries({ queryKey: ['wallet'] });
      qc.invalidateQueries({ queryKey: ['overview'] });
      setWithdraw('');
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message ?? 'Could not request the payout. Please try again.';
      toast.error(msg);
      // No bank connected yet? Open the connect form straight away.
      if (/payout bank account/i.test(msg)) setShowBank(true);
    },
  });

  const saveBank = useMutation({
    mutationFn: async () =>
      (await api.post('/me/wallet/payout-method', {
        accountNumber: bankNumber.replace(/\s/g, ''),
        sortCode: bankSort || undefined,
        accountName: bankName || undefined,
      })).data,
    onSuccess: () => {
      toast.success('Payout account connected.');
      setShowBank(false);
      qc.invalidateQueries({ queryKey: ['wallet'] });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.issues?.[0]?.message ?? e?.response?.data?.message ?? 'Could not save those bank details.');
    },
  });



  const weeklyEarnings = buildWeeklyEarnings(data.transactions);
  const balance = data.balanceGBP;
  const pending = data.pendingHoldGBP;
  const lifetime = data.lifetimeEarnedGBP ?? 0;
  const payouts = data.transactions.filter((t) => t.type === 'PAYOUT' || t.type === 'PAYOUT_PAID' || t.type === 'PAYOUT_REQUESTED');
  // A payout the seller has requested but an admin has not yet marked paid.
  const pendingPayouts = data.transactions.filter((t) => t.type === 'PAYOUT_REQUESTED').reduce((a, t) => a + Math.abs(t.amountGBP), 0);
  const refundedToYou = data.transactions.filter((t) => t.type === 'REFUND').reduce((a, t) => a + Math.abs(t.amountGBP), 0);
  const feesPaid = data.transactions.filter((t) => t.type === 'FEE').reduce((a, t) => a + Math.abs(t.amountGBP), 0);
  const filteredTx = data.transactions.filter((t) => {
    if (ledgerFilter === 'credits') return t.amountGBP > 0;
    if (ledgerFilter === 'fees') return t.type === 'FEE';
    if (ledgerFilter === 'payouts') return t.type.startsWith('PAYOUT');
    return true;
  });

  function downloadCsv() {
    const rows = [
      ['Date', 'Description', 'Type', 'Amount (GBP)'],
      ...data.transactions.map((t) => [
        new Date(t.date).toLocaleDateString(),
        `"${(t.description ?? '').replace(/"/g, "'")}"`,
        t.type,
        t.amountGBP.toFixed(2),
      ]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `souqli-wallet-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    if (!data.transactions.length) toast('No transactions to export yet.', { icon: 'ℹ️' });
  }

  return (
    <div className="space-y-6">
      {/* ─── Hero balance with chart background ─── */}
      <div className="section-dark relative text-cream overflow-hidden rounded-luxe" style={{ background: '#221A12' }}>
        <div className="absolute inset-0 bg-gold-mesh opacity-50" />
        <div className="absolute inset-0 dotgrid-light bg-[length:24px_24px] opacity-30" />
        {/* Decorative area chart */}
        <svg viewBox="0 0 1400 200" className="absolute inset-x-0 bottom-0 w-full opacity-25" preserveAspectRatio="none">
          <defs>
            <linearGradient id="hero-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#D98A2B" stopOpacity=".5" />
              <stop offset="1" stopColor="#D98A2B" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,160 L100,140 L200,150 L300,120 L400,135 L500,90 L600,110 L700,70 L800,85 L900,50 L1000,65 L1100,30 L1200,55 L1300,20 L1400,35 L1400,200 L0,200 Z" fill="url(#hero-area)" />
          <path d="M0,160 L100,140 L200,150 L300,120 L400,135 L500,90 L600,110 L700,70 L800,85 L900,50 L1000,65 L1100,30 L1200,55 L1300,20 L1400,35" fill="none" stroke="#D98A2B" strokeWidth="2" />
        </svg>
        <div className="relative px-5 py-8 sm:px-7 sm:py-10 grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <div className="stamp text-gold text-[9px]">AVAILABLE BALANCE</div>
            <div className="display text-[44px] sm:text-6xl md:text-7xl mt-2 leading-none">£{balance.toFixed(2)}</div>
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-mute-soft">
              <Pip color="bg-warn" k="Pending in payment hold" v={`£${pending.toFixed(2)}`} />
              <Pip color="bg-ok"   k="Lifetime earned"   v={`£${lifetime.toFixed(2)}`} />
              <Pip color="bg-clay-light" k="Souqli fees paid" v={`£${feesPaid.toFixed(2)}`} />
            </div>
            <div className="mt-6 flex flex-wrap gap-3 items-center">
              <div className="flex items-center bg-white border border-warmline rounded-luxe w-full sm:w-[300px] overflow-hidden focus-within:border-gold transition">
                <span className="pl-4 pr-1.5 text-ink display text-lg select-none">£</span>
                <input
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none py-2.5 pr-3 text-lg text-ink placeholder:text-mute"
                  type="number"
                  min={1}
                  max={balance}
                  value={withdraw || ''}
                  onChange={(e) => setWithdraw(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Amount"
                />
              </div>
              <button
                disabled={!withdraw || (typeof withdraw === 'number' && (withdraw > balance || withdraw <= 0)) || requestPayout.isPending}
                onClick={() => typeof withdraw === 'number' && requestPayout.mutate(withdraw)}
                className="btn btn-gold !text-ink"
              >
                {requestPayout.isPending ? 'Requesting…' : 'Request payout →'}
              </button>
              <button onClick={() => setWithdraw(balance)} className="btn text-cream border border-cream/30">Max £{balance.toFixed(2)}</button>
              <span className="stamp text-mute-soft text-[9px] ml-2">PAYOUTS ARE REVIEWED, THEN SENT TO YOUR BANK</span>
            </div>

            {/* There is no "add money" here by design - the wallet holds what
                Souqli owes you, so saying where it comes from prevents the
                obvious "how do I put money in?" question. */}
            <p className="text-[12px] text-mute-soft leading-relaxed mt-4 max-w-[52ch]">
              Souqli pays your sales into this wallet once the buyer confirms collection.
              Request a payout any time - we review it, then send it to your bank.
            </p>
          </div>

          {/* Connected payout method */}
          <div className="card-glass p-5" style={{ background: 'rgba(15,15,18,.5)', borderColor: 'rgba(201,169,97,.3)' }}>
            <div className="stamp text-gold text-[9px]">PAYOUT METHOD</div>
            {data.payoutMethod ? (
              <>
                <div className="mt-2.5 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg grid place-items-center text-2xl" style={{ background: 'linear-gradient(135deg,#D98A2B,#A66018)' }}>🏦</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-cream">Bank account</div>
                    <div className="stamp text-mute-soft text-[9px] mt-1 mono">·· ·· ·· {data.payoutMethod.last4}</div>
                  </div>
                  <span className="pill pill-ok text-[9px]">Verified</span>
                </div>
                <button onClick={() => setShowBank(true)} className="btn btn-sm w-full mt-4 text-cream border border-cream/30 text-center">Change account →</button>
              </>
            ) : (
              <div className="mt-2.5">
                <p className="text-[12px] text-mute-soft leading-relaxed">No payout method yet. Add the bank account you want your earnings paid into.</p>
                <button onClick={() => setShowBank(true)} className="btn btn-sm btn-gold w-full mt-3 text-center">Connect payouts →</button>
              </div>
            )}
          </div>
        </div>
        <div className="gold-rule" />
      </div>

      {/* ─── 4 stat tiles: what a seller tracks ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-5">
          <div className="stamp text-gold-dark text-[9px]">AVAILABLE BALANCE</div>
          <div className="display text-3xl mt-2">£{balance.toFixed(2)}</div>
          <div className="text-[11px] text-mute mt-2">Ready to withdraw</div>
        </div>
        <div className="card p-5 bg-clay/10 border-clay/30">
          <div className="stamp text-clay-dark text-[9px]">PENDING PAYOUTS</div>
          <div className="display text-3xl mt-2">£{pendingPayouts.toFixed(2)}</div>
          <div className="text-[11px] text-mute mt-2">{pendingPayouts > 0 ? 'Requested, awaiting review' : 'None requested'}</div>
        </div>
        <div className="card p-5 bg-sage/10 border-sage/30">
          <div className="stamp text-sage-dark text-[9px]">REFUNDED TO YOU</div>
          <div className="display text-3xl mt-2">£{refundedToYou.toFixed(2)}</div>
          <div className="text-[11px] text-mute mt-2">Returned to your wallet</div>
        </div>
        <div className="card p-5 bg-gold/10 border-gold/30">
          <div className="stamp text-gold-dark text-[9px]">LIFETIME EARNED</div>
          <div className="display text-3xl mt-2">£{Math.round(lifetime).toLocaleString()}</div>
          <div className="text-[11px] text-mute mt-2">Across all sales</div>
        </div>
      </div>

      <MoneyLedger variant="seller" className="mt-0" />

      {/* ─── Stacked earnings chart + Recent payouts ─── */}
      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="stamp text-gold-dark text-[9px]">EARNINGS BREAKDOWN · LAST 12 WEEKS</div>
              <h2 className="display text-2xl mt-1">Where your money came from</h2>
            </div>
            <div className="flex gap-3 text-[11px] text-mute">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-gold" />Sales</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-clay" />Swap credits</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-sage" />Refunds</span>
            </div>
          </div>
          <StackedBarChart weeks={weeklyEarnings} />
        </div>

        <div className="card p-6">
          <div className="stamp text-gold-dark text-[9px]">RECENT PAYOUTS</div>
          <h2 className="display text-2xl mt-1">Money home</h2>
          {payouts.length ? (
            <ul className="mt-4 space-y-3.5">
              {payouts.slice(0, 5).map((p) => (
                <li key={p.id} className="flex items-center gap-3 pb-3 border-b border-warmline last:border-0 last:pb-0">
                  <div className="w-8 h-8 rounded-full grid place-items-center text-sm bg-ok/15 text-ok">✓</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium">£{Math.abs(p.amountGBP).toFixed(2)}</div>
                    <div className="stamp text-mute text-[9px] mono">{new Date(p.date).toLocaleDateString()}</div>
                  </div>
                  <span className="pill pill-ok text-[9px]">{p.type.replace('_', ' ').toLowerCase()}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-xs text-mute leading-relaxed">No payouts yet. Once you make a sale and withdraw, your transfers show up here.</p>
          )}
        </div>
      </div>

      {/* ─── Detailed ledger ─── */}
      <section>
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="stamp text-gold-dark text-[9px]">LEDGER</div>
          <h2 className="display text-2xl mt-1">All transactions</h2>
        </div>
        <div className="flex gap-2">
          <select
            className="input w-auto text-[12px] py-1.5"
            value={ledgerFilter}
            onChange={(e) => setLedgerFilter(e.target.value as typeof ledgerFilter)}
          >
            <option value="all">All types</option>
            <option value="credits">Credits</option>
            <option value="fees">Fees</option>
            <option value="payouts">Payouts</option>
          </select>
          <button onClick={downloadCsv} className="btn btn-sm btn-outline">Download CSV</button>
        </div>
      </div>
      <div className="card overflow-x-auto">
        {filteredTx.length ? (
          <div className="min-w-[560px]">
            <div className="grid grid-cols-12 px-5 py-3 stamp text-mute text-[9px] border-b border-warmline bg-cream-deep">
              <div className="col-span-2">DATE</div>
              <div className="col-span-7">DESCRIPTION</div>
              <div className="col-span-2">TYPE</div>
              <div className="col-span-1 text-right">AMOUNT</div>
            </div>
            {filteredTx.map((t) => (
              <LedgerRow key={t.id} {...t} />
            ))}
          </div>
        ) : (
          <div className="px-5 py-14 text-center text-mute text-xs">
            {data.transactions.length
              ? 'No transactions match this filter.'
              : 'No transactions yet. Sales, payouts and fees will appear here as you use Souqli.'}
          </div>
        )}
      </div>
      </section>


      {/* ─── Connect payout bank account ─── */}
      {showBank && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setShowBank(false)}>
          <div className="bg-white rounded-2xl shadow-frame w-full max-w-[440px] overflow-hidden overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-warmline">
              <div className="stamp text-gold-dark text-[9px]">PAYOUT ACCOUNT</div>
              <h3 className="display text-2xl mt-1">Where should we send your money?</h3>
              <p className="text-[12px] text-mute mt-1.5">We only store the last 4 digits. Payouts arrive in 1-3 business days.</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label">Account holder name</label>
                <input className="input" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. Alice Seller" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Sort code</label>
                  <input className="input mono" value={bankSort} onChange={(e) => setBankSort(e.target.value)} placeholder="00-00-00" maxLength={8} />
                </div>
                <div>
                  <label className="label">Account number</label>
                  <input className="input mono" value={bankNumber} onChange={(e) => setBankNumber(e.target.value.replace(/[^\d]/g, ''))} placeholder="12345678" maxLength={10} />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowBank(false)} className="btn btn-outline flex-1">Cancel</button>
                <button
                  disabled={saveBank.isPending || bankNumber.replace(/\s/g, '').length < 6 || !/^\d{2}-?\d{2}-?\d{2}$/.test(bankSort)}
                  onClick={() => saveBank.mutate()}
                  className="btn btn-primary flex-[1.4]"
                >
                  {saveBank.isPending ? 'Saving…' : 'Save account'}
                </button>
              </div>
              <p className="text-[11px] text-mute text-center">🔒 Bank details are handled securely and never shared with buyers or sellers.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function Pip({ color, k, v }: { color: string; k: string; v: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      {k} <span className="display text-cream text-base">{v}</span>
    </span>
  );
}

function StackedBarChart({ weeks }: { weeks: { sales: number; swap: number; refund: number }[] }) {
  const barWidth = 36;
  const step = 49; // barWidth + gap
  const startX = 20;
  const chartH = 150;

  const totals = weeks.map((w) => w.sales + w.swap + w.refund);
  const max = Math.max(0, ...totals);
  const hasData = max > 0;

  // Round the axis ceiling up to a "nice" number so the gridline labels read cleanly.
  const magnitude = hasData ? 10 ** Math.floor(Math.log10(max)) : 1;
  const ceiling = hasData ? Math.ceil(max / magnitude) * magnitude : 100;
  const scale = chartH / ceiling;

  return (
    <svg viewBox="0 0 600 180" className="w-full h-40">
      <line x1="0" y1="150" x2="600" y2="150" stroke="#E8E2D5" />
      <line x1="0" y1="100" x2="600" y2="100" stroke="#E8E2D5" strokeDasharray="2 4" />
      <line x1="0" y1="50"  x2="600" y2="50"  stroke="#E8E2D5" strokeDasharray="2 4" />
      {weeks.map((w, i) => {
        const isCurrent = i === weeks.length - 1;
        const salesH = w.sales * scale;
        const swapH = w.swap * scale;
        const refundH = w.refund * scale;
        let y = chartH;
        const salesY = (y -= salesH);
        const swapY = (y -= swapH);
        const refundY = (y -= refundH);
        return (
          <g key={i} transform={`translate(${startX + i * step},0)`}>
            {salesH > 0 && <rect y={salesY} width={barWidth} height={salesH} fill={isCurrent ? '#0F0F12' : '#D98A2B'} rx="2" />}
            {swapH > 0 && <rect y={swapY} width={barWidth} height={swapH} fill="#C2614A" rx="2" />}
            {refundH > 0 && <rect y={refundY} width={barWidth} height={refundH} fill="#5C7A6B" rx="2" />}
          </g>
        );
      })}
      <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#9A9AA3" letterSpacing="1">
        {weeks.map((_, i) => {
          const isCurrent = i === weeks.length - 1;
          if (!isCurrent && i % 2 !== 0) return null;
          const x = startX + i * step + barWidth / 2;
          return (
            <text key={i} x={x} y="170" textAnchor="middle" fill={isCurrent ? '#0F0F12' : undefined}>
              {isCurrent ? 'NOW' : `W${i + 1}`}
            </text>
          );
        })}
        <text x="0" y="50"  fontSize="9">£{ceiling}</text>
        <text x="0" y="100" fontSize="9">£{Math.round(ceiling / 2)}</text>
        <text x="0" y="148" fontSize="9">£0</text>
      </g>
      {!hasData && (
        <text x="300" y="80" textAnchor="middle" fontSize="12" fill="#9A9AA3">No earnings yet - this fills in as you sell.</text>
      )}
    </svg>
  );
}

function LedgerRow({ date, description, type, amountGBP, ref, thumb, icon }: {
  id?: string; date: string; description: string; type: string; amountGBP: number; ref?: string; thumb?: string; icon?: string;
}) {
  const dateOnly = date.includes('/') ? date : new Date(date).toLocaleDateString();
  const typeMap: Record<string, string> = {
    CREDIT: 'pill-ok', RELEASE: 'pill-ok',
    FEE: 'pill-bad', REFUND: 'pill-bad', DEBIT: 'pill-bad',
    PAYOUT_REQUESTED: 'pill-gold', PAYOUT_PAID: 'pill-gold', PAYOUT: 'pill-gold',
    SWAP: 'pill-clay',
  };
  return (
    <div className="grid grid-cols-12 px-5 py-3 text-sm border-b border-warmline last:border-0 items-center hover:bg-cream-deep/50 transition">
      <div className="col-span-2 text-mute mono text-[12px]">{dateOnly}</div>
      <div className="col-span-7 flex items-center gap-3">
        {thumb ? (
          <div className="w-9 h-9 rounded notched-sm overflow-hidden bg-cover bg-center shrink-0" style={{ backgroundImage: `url(${thumb})` }} />
        ) : (
          <div className="w-9 h-9 rounded notched-sm bg-cream-deep grid place-items-center text-mute shrink-0">{icon ?? '•'}</div>
        )}
        <div>
          <div className="text-[13px]">{description}</div>
          {ref && <div className="stamp text-mute text-[9px]">{ref}</div>}
        </div>
      </div>
      <div className="col-span-2"><span className={`pill ${typeMap[type] ?? 'bg-cream-deep text-mute'} text-[9px]`}>{type.replace('_', ' ')}</span></div>
      <div className={`col-span-1 text-right display text-base ${amountGBP < 0 ? 'text-bad' : 'text-ok'}`}>
        {amountGBP < 0 ? '−' : '+'}£{Math.abs(amountGBP).toFixed(2)}
      </div>
    </div>
  );
}
